import * as React from 'react';
import useEstadoSistema from '../../hooks/useEstadoSistema';

/* ============================================================================
   Faixa de aviso do sistema.

   Tres avisos, um por vez, na ordem em que importam:

     1. SERVIDOR FORA DO AR   - nada mais funciona; e o unico que nao se fecha.
     2. VERSAO NOVA           - a aba esta com codigo antigo; recarregar resolve.
     3. API REINICIADA        - informativo; tarefa em andamento pode ter morrido.

   POR QUE UMA FAIXA, E NAO SNACKBAR: os tres sao ESTADO, nao evento. Snackbar
   aparece e vai embora; se a pessoa estava em outra aba do navegador quando a API
   caiu, ela volta e ve uma tela normal que nao salva nada. A faixa fica enquanto o
   estado durar.

   POR QUE NO TOPO, E EMPURRANDO O CONTEUDO: sobrepor esconde a primeira linha da
   tabela ou o cabecalho da tela. Aviso que tapa o conteudo custa mais do que
   informa.

   ---------------------------------------------------------------------------
   O "FECHAR" LEMBRA DA VERSAO, nao do aviso. Quem fecha o aviso de versao nova
   nao quer ser incomodado por AQUELA versao; se subir outra depois, o aviso volta.
   Sem isso, "fechar" viraria "nunca mais me avise", e a pessoa ficaria com a aba
   velha para sempre sem saber.

   A queda NAO tem fechar de proposito: nao ha nada a fazer na tela enquanto a API
   nao volta, e esconder o aviso so troca "sei que esta fora" por "acho que travou".
   ============================================================================ */

const CHAVE_DISPENSADO = 'gpshub:versao-dispensada';

export default function AvisoSistema() {
  const { fora, atualizado, reiniciado, versao } = useEstadoSistema(true);
  const [dispensada, setDispensada] = React.useState<string | null>(() => {
    try {
      return window.localStorage.getItem(CHAVE_DISPENSADO);
    } catch (e) {
      return null;
    }
  });

  const [reinicioVisto, setReinicioVisto] = React.useState(false);

  function dispensarVersao() {
    try {
      if (versao) window.localStorage.setItem(CHAVE_DISPENSADO, versao);
    } catch (e) {
      /* modo privado pode recusar - o aviso apenas volta na proxima carga */
    }
    setDispensada(versao ?? null);
  }

  // A ordem dos `if` E a prioridade: com a API fora, avisar de versao nova nao
  // ajuda em nada (recarregar tambem nao vai funcionar).
  if (fora) {
    return (
      <Faixa tom="danger" papel="alert">
        <strong>Sem conexão com o servidor.</strong> As telas mostram os últimos dados carregados, e nada que
        você salvar agora será gravado. Isto se resolve sozinho quando o servidor voltar.
      </Faixa>
    );
  }

  if (atualizado && versao !== dispensada) {
    return (
      <Faixa tom="info" papel="status" onFechar={dispensarVersao}>
        <strong>Uma versão nova do sistema foi publicada.</strong> Esta aba ainda usa a anterior — recarregue
        para usar a nova.
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="ml-2 rounded-md border border-current px-2 py-0.5 text-[11px] font-bold"
        >
          Recarregar agora
        </button>
      </Faixa>
    );
  }

  if (reiniciado && !reinicioVisto) {
    return (
      <Faixa tom="warn" papel="status" onFechar={() => setReinicioVisto(true)}>
        <strong>O servidor foi reiniciado.</strong> Sua sessão continua válida. Se você havia iniciado um envio
        para dispositivos, confira o resultado antes de repetir.
      </Faixa>
    );
  }

  return null;
}

/** Casca visual comum aos tres avisos. */
function Faixa({
  tom,
  papel,
  children,
  onFechar,
}: {
  tom: 'danger' | 'info' | 'warn';
  /** `alert` interrompe o leitor de tela; `status` espera a pausa. Queda e alert. */
  papel: 'alert' | 'status';
  children: React.ReactNode;
  onFechar?: () => void;
}) {
  const cores = {
    danger: 'border-danger bg-danger-soft text-danger',
    info: 'border-primary bg-primary/10 text-primary',
    warn: 'border-warn bg-warn-soft text-warn',
  }[tom];

  return (
    <div
      role={papel}
      aria-live={papel === 'alert' ? 'assertive' : 'polite'}
      className={`flex flex-wrap items-center gap-x-2 gap-y-1 border-b px-4 py-2 text-[12px] ${cores}`}
    >
      <span className="min-w-0 flex-1">{children}</span>
      {onFechar && (
        <button
          type="button"
          onClick={onFechar}
          aria-label="Fechar aviso"
          className="flex-none rounded-md px-1.5 text-[12.5px] font-bold opacity-70 hover:opacity-100"
        >
          ✕
        </button>
      )}
    </div>
  );
}
